import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Material } from '@babylonjs/core/Materials/material';
import type { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { Text } from '../../behaviors/text';
import type { NativeCopyOptions } from './native-copy';
import { FRAME_MS, openRunNow } from '../review/runtime';
import { pointedMesh, select } from './runtime-sequence';

/** Longest single `advanceTo` the session accepts: a caller loops for more, so a typo cannot hold a worker for hours. */
const MAX_ADVANCE_MS = 120_000;
/** Frame sums drift in floating point, so asking for the time it already is (to within this) is not asking to go back. */
const CLOCK_EPSILON_MS = 1e-6;

export type NativePointerEvent = 'hoverIn' | 'hoverOut' | 'down' | 'up' | 'select';

export interface NativeMaterialSnapshot {
    /** Linear RGB, 0 to 1 (the recording's convention). */
    color: [number, number, number] | null;
    emissive: [number, number, number] | null;
    /** Material alpha times the mesh's visibility. */
    opacity: number;
    /** Name of the base colour texture, or null. */
    texture: string | null;
}

export interface NativeTextSnapshot {
    string: string;
    family: string;
    size: number;
    weight: string;
    /** The resolved colour as the scene stores it (`#RRGGBB[AA]`). */
    color: string;
    /** Measured content box, metres. */
    box: { width: number; height: number };
}

export interface NativeNodeSnapshot {
    /** The node in the scene the session was opened on, not its copy. */
    object: SceneObject;
    path: string;
    /** The node and every ancestor are enabled. */
    visible: boolean;
    /** World pose: metres, `[x, y, z, w]` quaternion. */
    world: { position: [number, number, number]; rotation: [number, number, number, number]; scale: [number, number, number] };
    /** The first owned mesh that has a material, or null when the node draws nothing. */
    material: NativeMaterialSnapshot | null;
    text: NativeTextSnapshot | null;
}

export interface NativeRuntime {
    /** Elapsed scene time, milliseconds. */
    readonly nowMs: number;
    /**
     * Play forward to `ms` in 30 Hz frames (the last frame of a call is shorter when `ms` is off the frame grid). Scene time only moves
     * forward; asking for now again is a no-op. One call at a time: a second `advanceTo` while one is in flight rejects. `pointer` and
     * `snapshot` also throw while an advance is in flight, so await `advanceTo` before acting on the copy.
     */
    advanceTo(ms: number): Promise<void>;
    /**
     * Do what a pointer does to `target`: pseudo-states (`:hover`, `:active`, selection), and for hover and select the graph events
     * Babylon's blocks listen for (a graph has no press event, so `down` and `up` run `:active` only).
     * Hovering a second node leaves the first, as a moving pointer does. Throws while an advance is in flight.
     */
    pointer(target: SceneObject, event: NativePointerEvent): void;
    /**
     * What each node shows now. With no argument it builds an entry for every node in the copy, which is large on a big scene, so pass
     * the objects you need when sampling at 30 Hz. Throws while an advance is in flight.
     */
    snapshot(objects?: readonly SceneObject[]): NativeNodeSnapshot[];
    /**
     * Release the copy. The source is never touched. Every later call throws, and an `advanceTo` still in flight rejects before its next
     * frame renders. Calling it again does nothing.
     */
    dispose(): void;
}

const triple = (v: Vector3): [number, number, number] => [v.x, v.y, v.z];
const rgb = (c: Color3 | null | undefined): [number, number, number] | null => c ? [c.r, c.g, c.b] : null;

function readMaterial(mesh: AbstractMesh): NativeMaterialSnapshot | null {
    const material = mesh.material as (Material & {
        albedoColor?: Color3; diffuseColor?: Color3; emissiveColor?: Color3;
        albedoTexture?: { name: string } | null; diffuseTexture?: { name: string } | null;
    }) | null;
    if (!material) return null;
    return {
        color: rgb(material.albedoColor ?? material.diffuseColor),
        emissive: rgb(material.emissiveColor),
        opacity: material.alpha * mesh.visibility,
        texture: (material.albedoTexture ?? material.diffuseTexture)?.name ?? null,
    };
}

function readNode(object: SceneObject, source: SceneObject): NativeNodeSnapshot {
    // What is drawn sits under the animation root when a clip, spin or enter/exit motion owns one.
    const posed = object.animationRoot ?? object.transform;
    posed.computeWorldMatrix(true);
    const position = new Vector3(), scale = new Vector3(), rotation = new Quaternion();
    posed.getWorldMatrix().decompose(scale, rotation, position);
    const meshes = object.getOwnedMeshes();
    const text = object.getBehavior(Text);
    const resolved = text?._getResolvedData();
    const box = text?.getLayoutContentSize();
    return {
        object: source,
        path: object.getPath(),
        visible: object.transform.isEnabled(),
        world: { position: triple(position), rotation: [rotation.x, rotation.y, rotation.z, rotation.w], scale: triple(scale) },
        material: meshes.map(readMaterial).find(m => m !== null) ?? null,
        text: text && resolved ? {
            string: resolved.text ?? '', family: resolved.fontFamily ?? '', size: resolved.fontSize ?? 0,
            weight: resolved.fontWeight ?? '', color: resolved.color ?? '', box: { width: box?.x ?? 0, height: box?.y ?? 0 },
        } : null,
    };
}

/**
 * Open a disposable native copy of `source`, played from the start on an explicit clock, for comparing it with something else (a
 * recording) frame by frame. NullEngine only, so it runs without a browser. Scene documents load into a `TraceScene` the usual way
 * (`createSceneObjectFromData` on a NullEngine scene); this plays that scene. Scripts are refused, as in `probeNativeRuntime`.
 */
export async function openNativeRuntime(source: TraceScene, options: NativeCopyOptions = {}): Promise<NativeRuntime> {
    const run = await openRunNow(source, { copy: options });
    let hovered: SceneObject | null = null, disposed = false, advancing = false;
    const live = () => { if (disposed) throw new Error('This native runtime is disposed'); };
    const idle = () => { live(); if (advancing) throw new Error('An advanceTo is in progress: await it before another call'); };
    const copyOf = (object: SceneObject): SceneObject => {
        const copy = run.copy.get(object);
        if (!copy) throw new Error('Choose objects in the scene the runtime was opened on');
        return copy;
    };
    const scene = () => run.trace.getBabylonScene();
    const hover = (copy: SceneObject, entering: boolean) => {
        run.trace.handlePointerEvent('hover', copy, entering);
        const mesh = pointedMesh(copy);
        // The pointer-over block sees the mesh under the pointer, or null when it left.
        scene().onMeshUnderPointerUpdatedObservable.notifyObservers({ mesh: entering ? mesh : null, pointerId: 0 }, PointerEventTypes.POINTERMOVE);
    };
    return {
        get nowMs() { return run.nowMs; },
        async advanceTo(ms) {
            idle();
            if (!Number.isFinite(ms) || ms < run.nowMs - CLOCK_EPSILON_MS || ms - run.nowMs > MAX_ADVANCE_MS) throw new Error(`advanceTo takes a time up to ${MAX_ADVANCE_MS} ms ahead of now`);
            advancing = true;
            try {
                // One frame per await, so a dispose() during the wait is seen before the next frame renders.
                for (let left = ms - run.nowMs; left > CLOCK_EPSILON_MS;) {
                    const step = Math.min(FRAME_MS, left);
                    await run.advance(step);
                    live();
                    left -= step;
                }
            } finally { advancing = false; }
        },
        pointer(target, event) {
            idle();
            const copy = copyOf(target);
            if (event === 'hoverIn') {
                if (hovered && hovered !== copy) hover(hovered, false);
                hover(copy, true);
                hovered = copy;
            } else if (event === 'hoverOut') {
                // A pointer only leaves what it is over.
                if (hovered !== copy) return;
                hovered = null;
                hover(copy, false);
            } else if (event === 'down' || event === 'up') {
                // Pseudo-state only: no graph block listens to press, and nothing in a copy reads raw pointer down/up.
                run.trace.handlePointerEvent('active', copy, event === 'down');
            } else if (event === 'select') select(run.trace, copy);
            else throw new Error(`Unknown pointer event ${String(event)}`);
        },
        snapshot(objects) {
            idle();
            const nodes = objects ? objects.map(o => [copyOf(o), o] as const) : [...run.copy].map(([from, to]) => [to, from] as const);
            return nodes.map(([copy, from]) => readNode(copy, from));
        },
        dispose() {
            if (disposed) return;
            disposed = true;
            run.dispose();
        },
    };
}
